import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import ModalAlertaCompromisosLogin from '../components/Reutilizables/ModalAlertaCompromisosLogin';
import ModalAlertaCuotasVencidas from '../components/Reutilizables/ModalAlertaCuotasVencidas';
import RoleShell from '../components/layout/RoleShell';
import { roleNavigation } from '../config/roleNavigation';
import { getAdminAlertaCompromisosLogin } from '../js/adminGestionMorasService';
import { getAlertaCompromisosLogin, verificarGestionMoras } from '../js/asesorGestionMorasService';
import {
  cacheLoginAlert,
  getCachedLoginAlert,
  markLoginAlertAsShown,
  wasLoginAlertShown
} from '../utilities/gestionMorasSession';
import jwtUtils from '../utilities/jwtUtils';

function GestionMorasOverlays({ role }) {
  const isAdmin = role === 'admin';
  const isAsesor = role === 'asesor';
  const [showLoginAlert, setShowLoginAlert] = useState(false);
  const [loginAlert, setLoginAlert] = useState(null);
  const [loginAlertLoaded, setLoginAlertLoaded] = useState(false);
  const [showModalCuotasVencidas, setShowModalCuotasVencidas] = useState(false);
  const [totalPrestamosEnMora, setTotalPrestamosEnMora] = useState(0);

  useEffect(() => {
    setLoginAlert(null);
    setShowLoginAlert(false);
    setLoginAlertLoaded(false);

    if (!isAdmin && !isAsesor) {
      setLoginAlertLoaded(true);
      return undefined;
    }

    let cancelled = false;

    const cargarResumen = async () => {
      try {
        const cachedResumen = getCachedLoginAlert(role);
        if (cachedResumen) {
          setLoginAlert(cachedResumen);
          if (!cachedResumen.hayAlertas || wasLoginAlertShown(role)) {
            setShowLoginAlert(false);
            setLoginAlertLoaded(true);
            return;
          }

          markLoginAlertAsShown(role);
          setShowLoginAlert(true);
          setLoginAlertLoaded(true);
          return;
        }

        const resumen =
          role === 'admin'
            ? await getAdminAlertaCompromisosLogin()
            : await getAlertaCompromisosLogin();

        if (cancelled) {
          return;
        }

        cacheLoginAlert(role, resumen);
        setLoginAlert(resumen);
        if (!resumen.hayAlertas || wasLoginAlertShown(role)) {
          setShowLoginAlert(false);
          setLoginAlertLoaded(true);
          return;
        }

        markLoginAlertAsShown(role);
        setShowLoginAlert(true);
        setLoginAlertLoaded(true);
      } catch (error) {
        if (!cancelled) {
          setLoginAlertLoaded(true);
          console.error('Error al cargar la alerta de compromisos al iniciar sesión:', error);
        }
      }
    };

    cargarResumen();

    return () => {
      cancelled = true;
    };
  }, [isAdmin, isAsesor, role]);

  useEffect(() => {
    if (!isAsesor || !loginAlertLoaded || showLoginAlert) {
      return undefined;
    }

    let cancelled = false;

    const verificarPrestamos = async () => {
      try {
        const resultado = await verificarGestionMoras();
        if (cancelled) {
          return;
        }

        if (resultado.tienePrestamosEnMora) {
          setTotalPrestamosEnMora(resultado.totalPrestamos);
          setShowModalCuotasVencidas(true);
          return;
        }

        setTotalPrestamosEnMora(0);
        setShowModalCuotasVencidas(false);
      } catch (error) {
        if (!cancelled) {
          console.error('Error al verificar gestión de moras:', error);
        }
      }
    };

    verificarPrestamos();
    const intervalId = setInterval(verificarPrestamos, 2 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [isAsesor, loginAlertLoaded, showLoginAlert]);

  return (
    <>
      <ModalAlertaCompromisosLogin
        isOpen={showLoginAlert}
        onClose={() => setShowLoginAlert(false)}
        role={role}
        alert={loginAlert}
      />
      {isAsesor ? (
        <ModalAlertaCuotasVencidas
          isOpen={showModalCuotasVencidas}
          onClose={() => setShowModalCuotasVencidas(false)}
          totalPrestamosEnMora={totalPrestamosEnMora}
        />
      ) : null}
    </>
  );
}

const MainLayout = () => {
  const role = jwtUtils.getSessionRole();
  const menuItems = roleNavigation[role];

  if (!menuItems) {
    return <Navigate to="/401" replace />;
  }

  return (
    <RoleShell
      role={role}
      menuItems={menuItems}
      extraOverlay={role === 'admin' || role === 'asesor' ? <GestionMorasOverlays role={role} /> : null}
    />
  );
};

export default MainLayout;